import React, { Component, ContextType, ReactElement } from 'react';
import { createPortal } from 'react-dom';

import uuid from 'uuid';

import { DialogProps as DialogPropsService, DialogType } from '@philips-emr/hpms-stipulator-commons';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { DocumentContext } from '@philips-emr/tws-core/lib/component/context';
import { arrayOf, bool, element, eventListener, expression, object, oneOf, oneOfType } from '@philips-emr/tws-core/lib/component/prop-types';
import { AbortableCloseEvent, CloseEvent } from '@philips-emr/tws-core/lib/component/types';
import { classNames, enumValues, getSlot, isFunction } from '@philips-emr/tws-utils';

import { HpmsCustomDialogAPI } from '../../api';

import DialogActionDock from './component/dialog-action-dock';
import DialogContent from './component/dialog-content';
import DialogHeader from './component/dialog-header';
import { SubscriptionInformation } from './component/subscription-information';
import './hpms-custom-dialog.css';

export interface DialogProps extends DialogPropsService {
  children?: ReactElement | ReactElement[];
  className?: string,
  container?: Element,
  uuid: string;
  visible?: boolean;
}

interface DialogState {
  visible: boolean;
}

const SLOT_CONTENT = 'content';
const acceptedTypesDialogContent = enumValues(ComponentType) as ComponentType[];

const SLOT_DOCK = 'dock';

const acceptedTypesDialogDock = [
  ComponentType.NAVIGATION,
];

@AwesomeComponent({ type: ComponentType.LAYOUT, api: HpmsCustomDialogAPI })
export default class HpmsCustomDialog extends Component<DialogProps, DialogState> {

  public static contextType: any = DocumentContext;
  public context!: ContextType<typeof DocumentContext>;

  public state = {
    visible: true,
  };

  public static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    container: object.notEnumerable(),
    title: expression.placeholder('Title'),
    description: expression.notEnumerable(),
    dockButtons: arrayOf(element).notEnumerable(),
    type: oneOf(enumValues(DialogType)),
    onClose: eventListener,
    onBeforeClose: eventListener,
    visible: bool,
  } as any;

  public static defaultProps = {
    type: DialogType.DEFAULT,
    uuid: uuid(),
    visible: true,
  };

  componentDidMount(): void {
    this.setState({ visible: !!this.props.visible });
  }

  componentDidUpdate(prevProps: DialogProps, prevState: DialogState): void {
    const { visible } = this.props;
    const visiblePrevProps = prevProps.visible;
    const visiblePrevState = prevState.visible;
    const visibleState = this.state.visible;

    if ((visible !== visiblePrevProps) || (visible && visiblePrevProps && !visiblePrevState && !visibleState)) {
      this.setState({ visible: !!visible });
    }
  }

  private renderDialogContent(): ReactElement {
    const { uuid, description, children } = this.props;
    const dialogContentChildren = children && getSlot(children, SLOT_CONTENT);

    return (
      <DialogContent uuid={uuid} description={description} >
        {dialogContentChildren}
      </DialogContent>
    );
  }

  private renderDialogDock(): ReactElement {
    const { uuid, dockButtons, children } = this.props;
    const dialogDockChildren = children && getSlot(children, SLOT_DOCK);

    return (
      <DialogActionDock uuid={uuid} dockButtons={dockButtons} onClose={this.onModalClose} >
        {dialogDockChildren}
      </DialogActionDock>
    );
  }

  private onModalClose = (): void => {
    const { onClose, onBeforeClose } = this.props;
    let close = true;

    if (isFunction(onBeforeClose) && this.state.visible) {
      const abort = (): void => {
        close = false;
      };
      onBeforeClose(new AbortableCloseEvent({ abort }));
    }

    if (close) {
      if (isFunction(onClose)) {
        onClose(new CloseEvent());
      }

      this.setState(({ visible }) => ({ visible: !visible }));
    }
  };

  public renderDialog(): ReactElement | null {
    const Container = ContainerProvider.provide(this);

    if (!Container) {
      return null;
    }

    const { title, type, uuid } = this.props;

    const dialogContext = {
      ...this.context,
    };

    return (
      <DocumentContext.Provider value={dialogContext}>
        <div
          className={this.getClassNameDialog()}
          tabIndex={0}
        >
          <div className="c-dialog__wrapper">
            <SubscriptionInformation type={type} />

            <DialogHeader title={title} type={type} />

            <div className={this.getClassNameContent()}>
              <Container acceptedTypes={acceptedTypesDialogContent} uuid={uuid} slot={SLOT_CONTENT} >
                {this.renderDialogContent()}
              </Container>

              <Container acceptedTypes={acceptedTypesDialogDock} uuid={uuid} slot={SLOT_DOCK} >
                {this.renderDialogDock()}
              </Container>
            </div>
          </div>
        </div>
      </DocumentContext.Provider>
    );
  }

  render(): ReactElement | null {
    const { document } = this.context;
    const { container = document?.body } = this.props;
    const { visible } = this.state;

    if (!container || !visible) {
      return null;
    }

    return createPortal(this.renderDialog(), container);
  }

  private getClassNameDialog(): string {
    const { className } = this.props;

    const dialogClasses = {
      'c-dialog': true,
      [`${className}`]: Boolean(className),
    };

    return classNames(dialogClasses);
  }

  private getClassNameContent(): string {
    return classNames({
      'c-dialog__content-wrapper': true,
      'u-no-padding': true,
      'u-overflow-auto': true,
    });
  }

}
